import { useState } from 'react'
import { isFirefox } from '../detectFirefox'

const ACKNOWLEDGED_STORAGE_KEY = 'neeno.firefox-ble-acknowledged'

function hasAcknowledgedFirefoxNotice(): boolean {
  if (typeof window === 'undefined') return true
  return window.localStorage.getItem(ACKNOWLEDGED_STORAGE_KEY) === '1'
}

function rememberFirefoxNotice(): void {
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(ACKNOWLEDGED_STORAGE_KEY, '1')
  }
}

/**
 * FirefoxBleNotice — one-time notice shown to Firefox users.
 * Firefox does not ship the Web Bluetooth API, so BLE sensor connections are
 * unavailable there. The notice explains the limitation and lets the user
 * continue once they have acknowledged it.
 */
export function FirefoxBleNotice() {
  const [acknowledged, setAcknowledged] = useState(
    () => !isFirefox() || hasAcknowledgedFirefoxNotice(),
  )

  if (acknowledged) return null

  const onContinue = () => {
    rememberFirefoxNotice()
    setAcknowledged(true)
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4"
      role="dialog"
      aria-modal="true"
      aria-label="Browser compatibility notice"
    >
      <div className="w-full max-w-md rounded-[1.75rem] border border-edge/70 bg-surface p-6 shadow-panel sm:p-7">
        <p className="eyebrow text-accent">Browser check</p>
        <h2 className="mt-1.5 text-lg font-medium tracking-tight text-primary">
          Web Bluetooth not supported
        </h2>
        <p className="mt-3 text-sm text-primary">
          It looks like you're using Firefox. Firefox doesn't support the Web Bluetooth API, which
          NEENO Lab uses to connect to its sensors.
        </p>
        <p className="mt-2 text-sm text-muted">
          You can still explore the rest of NEENO Lab, but connecting devices won't be available in
          this browser. For the full experience, we recommend Chrome or Edge.
        </p>
        <button type="button" onClick={onContinue} className="pill-solid mt-6 w-full">
          I understand — continue
        </button>
      </div>
    </div>
  )
}
